import type {
  Notification,
  NotificationPayload,
  NotificationType,
  ActivityNotificationType,
} from '@/types/notification';
import { supabase } from '../supabase';
import { CACHE_TTL, getCacheKey, supabaseCache } from './cache';
import { safeSupabaseOperation } from './error-handler';
import type { RealtimeChannel } from '@supabase/supabase-js';

/** Tipos que alimentan la bandeja de Actividad en /chats */
const ACTIVITY_TYPES: ActivityNotificationType[] = [
  'connection_request_received',
  'connection_request_sent',
  'connection_accepted',
  'chat_message',
  'meetup_confirmed',
];

const mapNotification = (row: any): Notification => ({
  id: row.id,
  user_id: row.user_id,
  type: row.type,
  payload: row.payload ?? {},
  read_at: row.read_at,
  created_at: row.created_at,
});

export type ListActivityEventsOptions = {
  onlyUnread?: boolean;
  limit?: number;
  cursor?: string;
};

export type ListActivityEventsResult = {
  events: Notification[];
  nextCursor: string | null;
};

export const notificationsService = {
  getNotifications: async (userId: string, limit: number = 25): Promise<Notification[]> => {
    const cacheKey = getCacheKey('notifications', userId, limit);
    const cached = supabaseCache.get<Notification[]>(cacheKey);
    if (cached) return cached;

    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .select('id, user_id, type, payload, read_at, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(limit),
      'Error al obtener notificaciones'
    );

    if (result.error || !result.data) {
      return [];
    }

    const notifications = (result.data as any[]).map(mapNotification);
    supabaseCache.set(cacheKey, notifications, CACHE_TTL.DYNAMIC);
    return notifications;
  },

  getUnreadCount: async (userId: string): Promise<number> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .select('id')
          .eq('user_id', userId)
          .is('read_at', null),
      'Error al obtener notificaciones no leídas'
    );

    if (result.error || !result.data) {
      return 0;
    }

    return (result.data as { id: string }[]).length;
  },

  /** Lista eventos de la bandeja Actividad (solo tipos actividad). Solo eventos del usuario. */
  listActivityEvents: async (
    userId: string,
    options: ListActivityEventsOptions = {}
  ): Promise<ListActivityEventsResult> => {
    const { onlyUnread = false, limit = 30, cursor } = options;
    const cacheKey = getCacheKey('activity_events', userId, onlyUnread, limit, cursor ?? '');
    const cached = supabaseCache.get<ListActivityEventsResult>(cacheKey);
    if (cached) return cached;

    let query = supabase
      .from('notifications')
      .select('id, user_id, type, payload, read_at, created_at')
      .eq('user_id', userId)
      .in('type', ACTIVITY_TYPES)
      .order('created_at', { ascending: false })
      .limit(limit + 1);

    if (onlyUnread) {
      query = query.is('read_at', null);
    }
    if (cursor) {
      query = query.lt('created_at', cursor);
    }

    const result = await safeSupabaseOperation(
      () => query,
      'Error al obtener actividad'
    );

    if (result.error || !result.data) {
      return { events: [], nextCursor: null };
    }

    const rows = result.data as any[];
    const hasMore = rows.length > limit;
    const events = (hasMore ? rows.slice(0, limit) : rows).map(mapNotification);
    const nextCursor =
      hasMore && events.length > 0
        ? events[events.length - 1].created_at
        : null;

    const out: ListActivityEventsResult = { events, nextCursor };
    supabaseCache.set(cacheKey, out, CACHE_TTL.DYNAMIC);
    return out;
  },

  /** Cuenta de no leídos solo para tipos de actividad (bandeja Actividad). */
  getActivityUnreadCount: async (userId: string): Promise<number> => {
    const cacheKey = getCacheKey('activity_unread_count', userId);
    const cached = supabaseCache.get<number>(cacheKey);
    if (cached !== undefined) return cached;

    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .select('id')
          .eq('user_id', userId)
          .in('type', ACTIVITY_TYPES)
          .is('read_at', null),
      'Error al obtener cuenta de actividad no leída'
    );

    if (result.error || !result.data) {
      return 0;
    }

    const count = (result.data as { id: string }[]).length;
    supabaseCache.set(cacheKey, count, CACHE_TTL.DYNAMIC);
    return count;
  },

  /**
   * Remitentes distintos con mensajes de chat no leídos (estilo WhatsApp:
   * el badge cuenta conversaciones, no mensajes sueltos).
   */
  getUnreadChatSenderIds: async (userId: string): Promise<string[]> => {
    const cacheKey = getCacheKey('unread_chat_senders', userId);
    const cached = supabaseCache.get<string[]>(cacheKey);
    if (cached) return cached;

    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .select('payload')
          .eq('user_id', userId)
          .eq('type', 'chat_message')
          .is('read_at', null)
          .limit(500),
      'Error al obtener chats no leídos'
    );

    if (result.error || !result.data) {
      return [];
    }

    const ids = new Set<string>();
    for (const row of result.data as { payload?: NotificationPayload | null }[]) {
      const senderId = row.payload?.sender_id;
      if (typeof senderId === 'string' && senderId) ids.add(senderId);
    }
    const list = Array.from(ids);
    supabaseCache.set(cacheKey, list, CACHE_TTL.DYNAMIC);
    return list;
  },

  /** Marcar un evento de actividad como leído (por id). RLS asegura que solo el destinatario puede. */
  markActivityEventRead: async (eventId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('id', eventId),
      'Error al marcar notificación como leída'
    );
    if (!result.error) supabaseCache.invalidatePattern('notifications:*');
    if (!result.error) supabaseCache.invalidatePattern('unread_chat_senders:*');
    return !result.error;
  },

  /** Marcar todos los eventos de actividad del usuario como leídos. */
  markAllActivityRead: async (userId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('user_id', userId)
          .in('type', ACTIVITY_TYPES)
          .is('read_at', null),
      'Error al marcar toda la actividad como leída'
    );

    if (!result.error) {
      supabaseCache.invalidatePattern(`notifications:${userId}:*`);
      supabaseCache.invalidatePattern('activity_events:*');
      supabaseCache.invalidatePattern('activity_unread_count:*');
      supabaseCache.invalidatePattern('unread_chat_senders:*');
    }

    return !result.error;
  },

  /** Eliminar un evento de actividad (por id). RLS asegura que solo el destinatario puede. */
  deleteActivityEvent: async (eventId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .delete()
          .eq('id', eventId),
      'Error al eliminar notificación de actividad'
    );

    if (!result.error) {
      supabaseCache.invalidatePattern('notifications:*');
      supabaseCache.invalidatePattern('activity_events:*');
      supabaseCache.invalidatePattern('activity_unread_count:*');
      supabaseCache.invalidatePattern('unread_chat_senders:*');
    }

    return !result.error;
  },

  getEventNotifications: async (userId: string, eventId: string, limit: number = 25): Promise<Notification[]> => {
    const cacheKey = getCacheKey('event_notifications', userId, eventId, limit);
    const cached = supabaseCache.get<Notification[]>(cacheKey);
    if (cached) return cached;

    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .select('id, user_id, type, payload, read_at, created_at')
          .eq('user_id', userId)
          .eq('type', 'event_notification')
          .filter('payload->>event_id', 'eq', eventId)
          .order('created_at', { ascending: false })
          .limit(limit),
      'Error al obtener notificaciones del evento'
    );

    if (result.error || !result.data) {
      return [];
    }

    const notifications = (result.data as any[]).map(mapNotification);
    supabaseCache.set(cacheKey, notifications, CACHE_TTL.DYNAMIC);
    return notifications;
  },

  markAsRead: async (notificationId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('id', notificationId),
      'Error al marcar notificación como leída'
    );

    if (!result.error) {
      supabaseCache.invalidatePattern('notifications:*');
      supabaseCache.invalidatePattern('unread_chat_senders:*');
    }

    return !result.error;
  },

  markAllAsRead: async (userId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('user_id', userId)
          .is('read_at', null),
      'Error al marcar todas las notificaciones'
    );

    if (!result.error) {
      supabaseCache.invalidatePattern(`notifications:${userId}:*`);
      supabaseCache.invalidatePattern('unread_chat_senders:*');
    }

    return !result.error;
  },

  createNotification: async (params: {
    userId: string;
    type: NotificationType;
    payload: NotificationPayload;
  }): Promise<boolean> => {
    const { userId, type, payload } = params;
    
    const result = await safeSupabaseOperation(
      () =>
        supabase.from('notifications').insert({
          user_id: userId,
          type: type as string, // Asegurar que es string
          payload,
        }),
      'Error al crear notificación'
    );

    if (result.error) {
      return false;
    }

    supabaseCache.invalidatePattern(`notifications:${userId}:*`);
    if (ACTIVITY_TYPES.includes(type as ActivityNotificationType)) {
      supabaseCache.invalidatePattern(`activity_events:${userId}:*`);
      supabaseCache.invalidatePattern(`activity_unread_count:${userId}`);
    }
    if (type === 'chat_message') {
      supabaseCache.invalidatePattern('unread_chat_senders:*');
    }

    return true;
  },

  subscribe: (
    userId: string,
    onNotification: (notification: Notification) => void,
    onError?: (error: Error) => void
  ): (() => void) => {
    const channel: RealtimeChannel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const notification = mapNotification(payload.new);
          supabaseCache.invalidatePattern(`notifications:${userId}:*`);
          onNotification(notification);
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          onError?.(new Error('Error en la suscripción de notificaciones'));
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  },
};

